/**
 * Calculator Page - Main multi-step form component
 */

import React, { useState, useEffect } from 'react';
import { FormData, StateInfo, PricingInfo } from '../types';
import { calculatorAPI, stripeAPI } from '../services/api';
import BasicInfoForm from '../components/BasicInfoForm';
import AssetsForm from '../components/AssetsForm';
import LiabilitiesForm from '../components/LiabilitiesForm';
import IncomeForm from '../components/IncomeForm';
import ChildrenForm from '../components/ChildrenForm';
import PreMaritalForm from '../components/PreMaritalForm';
import SpecialFactorsForm from '../components/SpecialFactorsForm';
import PreviewAndPayment from '../components/PreviewAndPayment';

const Calculator: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState<StateInfo[]>([]);
  const [pricing, setPricing] = useState<PricingInfo | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Form data state
  const [formData, setFormData] = useState<FormData>({
    email: '',
    firstName: '',
    lastName: '',
    marriageDate: '',
    separationDate: '',
    state: '',
    userIncome: 0,
    spouseIncome: 0,
    hasChildren: false,
    numberOfChildren: 0,
    primaryCaregiver: 'joint',
    hasSpecialNeeds: false,
    userPreMaritalAssets: 0,
    spousePreMaritalAssets: 0,
    spouseWastedAssets: false,
    userWastedAssets: false,
    userStayAtHome: false,
    userSupportedSpouseCareer: false,
    multiState: false,
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [statesData, pricingData] = await Promise.all([
        calculatorAPI.getStates(),
        calculatorAPI.getPricing(),
      ]);
      setStates(statesData);
      setPricing(pricingData);
    } catch (error) {
      console.error('Failed to load initial data:', error);
    }
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    setCurrentStep(prev => Math.min(prev + 1, 8));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmitForm = async () => {
    setLoading(true);
    try {
      const response = await calculatorAPI.submit(formData.state, formData);
      setSessionId(response.sessionId);
      setCurrentStep(8);
    } catch (error) {
      console.error('Form submission failed:', error);
      alert('Failed to submit form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!sessionId) {
      alert('Please submit your form first.');
      return;
    }

    setLoading(true);
    try {
      const response = await stripeAPI.createCheckoutSession(formData, sessionId);
      // Redirect to Stripe Checkout
      window.location.href = response.url;
    } catch (error) {
      console.error('Payment initialization failed:', error);
      alert('Failed to initialize payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const progress = ((currentStep - 1) / 7) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Property Division Calculator
          </h1>
          <p className="text-gray-600">
            Step {currentStep} of 8
          </p>
        </div>

        {/* Progress Bar */}
        <div className="max-w-3xl mx-auto mb-8">
          <div className="bg-gray-200 rounded-full h-3">
            <div
              className="bg-blue-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-600">
            <span>Basic Info</span>
            <span>Assets</span>
            <span>Liabilities</span>
            <span>Income</span>
            <span>Children</span>
            <span>Pre-Marital</span>
            <span>Special</span>
            <span>Payment</span>
          </div>
        </div>

        {/* Form Container */}
        <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-lg p-6 md:p-8">
          {currentStep === 1 && (
            <BasicInfoForm
              formData={formData}
              onChange={handleInputChange}
              onNext={handleNext}
              states={states}
            />
          )}

          {currentStep === 2 && (
            <AssetsForm
              formData={formData}
              onChange={handleInputChange}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}

          {currentStep === 3 && (
            <LiabilitiesForm
              formData={formData}
              onChange={handleInputChange}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}

          {currentStep === 4 && (
            <IncomeForm
              formData={formData}
              onChange={handleInputChange}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}

          {currentStep === 5 && (
            <ChildrenForm
              formData={formData}
              onChange={handleInputChange}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}

          {currentStep === 6 && (
            <PreMaritalForm
              formData={formData}
              onChange={handleInputChange}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}

          {currentStep === 7 && (
            <SpecialFactorsForm
              formData={formData}
              onChange={handleInputChange}
              onNext={handleSubmitForm}
              onBack={handleBack}
              loading={loading}
            />
          )}

          {currentStep === 8 && sessionId && (
            <PreviewAndPayment
              formData={formData}
              pricing={pricing}
              onPayment={handlePayment}
              loading={loading}
              onBack={handleBack}
            />
          )}
        </div>

        {/* Privacy Notice */}
        <div className="max-w-3xl mx-auto mt-6 text-center text-sm text-gray-600">
          <p>
            🔒 <strong>Your Privacy:</strong> We do not store any personal information. Your data is
            only used to generate your report and is immediately deleted.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Calculator;
